import type { PortableTextBlock } from "@portabletext/types";
import type {
  ContentGridColumn,
  ContentGridColumnItem,
  ContentGridItem,
} from "./form-types";
import type { StepInputRendererProps } from "./StepInputRenderer";

import { cn } from "@homeinstead/ui";
import { RichText } from "@homeinstead/ui/components/RichText";
import Image from "next/image";
import { AnimatedReveal } from "../../AnimatedReveal/AnimatedReveal";

const GRID_COLS_CLASS: Record<number, string> = {
  1: "md:grid-cols-1",
  2: "md:grid-cols-2",
  3: "md:grid-cols-3",
  4: "md:grid-cols-4",
};

const ICON_ITEM_BOTTOM_PADDING_CLASSES: Record<string, string> = {
  lg: "pb-14",
  md: "pb-10",
  "md-xl-responsive": "pb-10 md:pb-24",
  sm: "pb-6",
  xl: "pb-24",
};

function getGridColsClass(columnCount: number): string {
  return GRID_COLS_CLASS[columnCount] ?? GRID_COLS_CLASS[4];
}

type IconItemData = Extract<ContentGridColumnItem, { _type: "iconItem" }>;
type RichTextBlockData = Extract<
  ContentGridColumnItem,
  { _type: "richTextBlock" }
>;

function IconCell({
  item,
  placeholderValues,
}: {
  item: IconItemData;
  placeholderValues: StepInputRendererProps["placeholderValues"];
}) {
  const captionBlocks = Array.isArray(item.caption)
    ? (item.caption as PortableTextBlock[])
    : [];

  return (
    <div className="flex flex-row items-start gap-8 md:flex-col md:items-center md:gap-4">
      {item.iconUrl ? (
        <Image
          alt=""
          className="h-auto w-auto shrink-0"
          height={0}
          src={item.iconUrl}
          width={0}
        />
      ) : null}
      {captionBlocks.length > 0 ? (
        <RichText placeholders={placeholderValues} value={captionBlocks} />
      ) : null}
    </div>
  );
}

function RichTextCell({
  item,
  placeholderValues,
}: {
  item: RichTextBlockData;
  placeholderValues: StepInputRendererProps["placeholderValues"];
}) {
  const textBlocks = Array.isArray(item.text)
    ? (item.text as PortableTextBlock[])
    : [];

  if (textBlocks.length === 0) {
    return null;
  }

  return <RichText placeholders={placeholderValues} value={textBlocks} />;
}

function getIconItemBottomPaddingClass(
  verticalPadding?: IconItemData["verticalPadding"],
): string {
  return ICON_ITEM_BOTTOM_PADDING_CLASSES[verticalPadding ?? ""] ?? "";
}

function ColumnItems({
  column,
  placeholderValues,
}: {
  column: ContentGridColumn;
  placeholderValues: StepInputRendererProps["placeholderValues"];
}) {
  const items = column.items ?? [];

  return (
    <div className="flex flex-col gap-2">
      {items.map((item) => {
        if (item._type === "iconItem") {
          return (
            <AnimatedReveal animation={item.animation} key={item._key}>
              <div
                className={getIconItemBottomPaddingClass(item.verticalPadding)}
              >
                <IconCell item={item} placeholderValues={placeholderValues} />
              </div>
            </AnimatedReveal>
          );
        }

        if (item._type === "richTextBlock") {
          return (
            <RichTextCell
              item={item as RichTextBlockData}
              key={item._key}
              placeholderValues={placeholderValues}
            />
          );
        }

        return null;
      })}
    </div>
  );
}

function ContentGridFormItemInner({
  input,
  placeholderValues,
}: StepInputRendererProps) {
  if (input._type !== "contentGrid") {
    return null;
  }

  const grid = input as ContentGridItem;
  const columns = grid.columns ?? [];

  if (columns.length === 0) {
    return null;
  }

  return (
    <div
      className={cn("grid grid-cols-1 gap-6", getGridColsClass(columns.length))}
    >
      {columns.map((column) => (
        <div key={column._key}>
          <ColumnItems column={column} placeholderValues={placeholderValues} />
        </div>
      ))}
    </div>
  );
}

export { ContentGridFormItemInner as ContentGridFormItem };
